import { effect, inject, Injectable, signal } from "@angular/core";
import { ITranslateObject, JobsBillingCard } from "../views-interfaces";
import { MultiSelectModel, Option, type OptionValue } from "@app/filter-menu/filter-types";
import { IFirmRolesService, PARTNER_ROLE_ID, compareByActiveAndName } from "@app/shared";
import { CardsService, JOB_PARTNER_COUNT_KEY } from "@services/index";

@Injectable()
export class JobsPartnerFilterGeneratorService {
    private readonly iFirmRolesService = inject(IFirmRolesService);
    private readonly cardsService = inject(CardsService);

    public readonly model = signal<MultiSelectModel | null>(null);
    private readonly selection = signal<Option[]>([]);
    private readonly reload = signal<boolean>(true);

    constructor() {
        effect(() => {
            if (!this.reload()) return;
            const selection = this.selection();
            this.model.set(this.generateModel(selection));
        });
    }

    public loadModel(selection: Option[]): void {
        this.reload.set(false);
        this.selection.set(selection ?? []);
        this.reload.set(true);
    }

    public updateSelection(selection: Option[]): void {
        this.selection.set(selection ?? []);
    }

    public generateModel(selection: Option[]): MultiSelectModel {
        const options: Option[] = [
            {
                text: "General.None",
                value: "-1",
                isVisible: (_: unknown) => true,
            },
        ];

        const roles = this.iFirmRolesService.rolesWithUsers();
        if (roles?.has(PARTNER_ROLE_ID)) {
            const users = Array.from(roles.get(PARTNER_ROLE_ID)?.Users.values() ?? []);
            users.sort(compareByActiveAndName);
            const optionsToAdd: Option[] = users.map((user) => ({
                text: user.FullName ?? `${user.FirstName} ${user.LastName}`,
                value: user.IFirmUserId,
                isVisible: (_: unknown) =>
                    user.Active || (this.cardsService.getCardsCountForUser(user).totalByRole?.[JOB_PARTNER_COUNT_KEY] ?? 0) > 0,
            }));

            options.push(...optionsToAdd);
        }

        const applicableOptions = options.filter((option) =>
            option.isVisible ? (typeof option.isVisible === "boolean" ? option.isVisible : option.isVisible(undefined)) : true,
        );

        return {
            label: {
                "en-CA": "Job Partner",
                "fr-CA": "Associé responsable",
            },
            applicableOptions,
            allOptions: options,
            selection: selection.filter((selected) => applicableOptions.some((applicable) => selected.value === applicable.value)),
        } as MultiSelectModel;
    }

    public getPrintDescription(
        label: string | ITranslateObject,
        selectedOptions: Option[],
    ): { label: string | ITranslateObject; values: (string | ITranslateObject)[] } | null {
        if (!selectedOptions || selectedOptions.length === 0) {
            return null;
        } else {
            return {
                label,
                values: selectedOptions.map((value) => {
                    return value.text;
                }),
            };
        }
    }

    public generateFilter(selectedValues: OptionValue[]) {
        if (!selectedValues || selectedValues.length === 0) {
            return null;
        } else {
            return (card: JobsBillingCard) => {
                const Id = card.job.partner;
                return (Id === null && selectedValues.includes("-1")) || selectedValues.includes(Id);
            };
        }
    }
}
